import { TFunction } from 'next-i18next/pages';

import { formatDistance } from './format-distance';

export const DEFAULT_MAX_DISTANCE_KM = 30;
const MIN_MONTHLY_SALARY = 1000;

export type MatchReasonKey = 'distance' | 'salary' | 'shift';

export interface MatchReason {
  key: MatchReasonKey;
  text: string;
}

export interface GetMatchReasonsInput {
  distanceKm?: number | null;
  maxDistanceKm?: number | null;
  salaryExpectation?: number | null;
  salaryLow?: number | null;
  salaryHigh?: number | null;
  canShowSalary: boolean;
  shiftMatch?: boolean | null;
  formalLanguage?: boolean;
}

const formalKey = (key: string, formalLanguage?: boolean): string => `${key}.${formalLanguage ? 'formal' : 'informal'}`;

const isDefined = (value: number | null | undefined): value is number => value !== null && value !== undefined;

const isMonthlySalary = (value: number | null | undefined): value is number => isDefined(value) && value >= MIN_MONTHLY_SALARY;

const isWithinSearchRadius = (distanceKm: number, maxDistanceKm?: number | null): boolean =>
  distanceKm <= (isDefined(maxDistanceKm) && maxDistanceKm > 0 ? maxDistanceKm : DEFAULT_MAX_DISTANCE_KM);

const getSalaryTextKey = (salaryExpectation: number, salaryLow: number, salaryHigh?: number | null): string | null => {
  if (salaryLow >= salaryExpectation) return 'matchReasons.salary.above';
  if (isMonthlySalary(salaryHigh) && salaryHigh >= salaryExpectation) return 'matchReasons.salary.within';
  return null;
};

export const getMatchReasons = (t: TFunction, input: GetMatchReasonsInput): MatchReason[] => {
  const { distanceKm, maxDistanceKm, salaryExpectation, salaryLow, salaryHigh, canShowSalary, shiftMatch, formalLanguage } = input;
  const reasons: MatchReason[] = [];

  const distance = isDefined(distanceKm) && isWithinSearchRadius(distanceKm, maxDistanceKm) ? formatDistance(distanceKm) : null;
  if (distance) {
    reasons.push({ key: 'distance', text: t(formalKey('matchReasons.distance', formalLanguage), { distance }) });
  }

  const salaryTextKey =
    canShowSalary && isMonthlySalary(salaryExpectation) && isMonthlySalary(salaryLow)
      ? getSalaryTextKey(salaryExpectation, salaryLow, salaryHigh)
      : null;
  if (salaryTextKey) {
    reasons.push({ key: 'salary', text: t(formalKey(salaryTextKey, formalLanguage)) });
  }

  if (shiftMatch === true) {
    reasons.push({ key: 'shift', text: t(formalKey('matchReasons.shift', formalLanguage)) });
  }

  return reasons;
};
